import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

interface UseAutoSaveOptions<T> {
  entityType: string;
  entityId?: string;
  data: T;
  debounceMs?: number;
  enabled?: boolean;
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export function useAutoSave<T>({
  entityType,
  entityId,
  data,
  debounceMs = 2000,
  enabled = true,
}: UseAutoSaveOptions<T>) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousDataRef = useRef<string>("");

  const saveDraft = useCallback(async (draftData: T) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      setStatus("saving");

      // Check if draft exists
      const query = supabase
        .from("content_drafts")
        .select("id")
        .eq("user_id", session.user.id)
        .eq("entity_type", entityType);
      
      if (entityId) {
        query.eq("entity_id", entityId);
      } else {
        query.is("entity_id", null);
      }

      const { data: existing } = await query.maybeSingle();

      if (existing) {
        // Update existing draft
        const { error } = await supabase
          .from("content_drafts")
          .update({
            draft_data: draftData as Json,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        // Insert new draft
        const { error } = await supabase
          .from("content_drafts")
          .insert([{
            user_id: session.user.id,
            entity_type: entityType,
            entity_id: entityId || null,
            draft_data: draftData as Json,
          }]);
        if (error) throw error;
      }

      setStatus("saved");
      setLastSaved(new Date());
    } catch (error) {
      console.error("Auto-save error:", error);
      setStatus("error");
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (!enabled) return;

    const currentData = JSON.stringify(data);
    
    // Skip if data hasn't changed
    if (currentData === previousDataRef.current) return;
    previousDataRef.current = currentData;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      saveDraft(data);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, debounceMs, enabled, saveDraft]);

  const loadDraft = useCallback(async (): Promise<T | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const query = supabase
        .from("content_drafts")
        .select("draft_data")
        .eq("user_id", session.user.id)
        .eq("entity_type", entityType);

      if (entityId) {
        query.eq("entity_id", entityId);
      } else {
        query.is("entity_id", null);
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;
      return data?.draft_data as T | null;
    } catch (error) {
      console.error("Load draft error:", error);
      return null;
    }
  }, [entityType, entityId]);

  const clearDraft = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const query = supabase
        .from("content_drafts")
        .delete()
        .eq("user_id", session.user.id)
        .eq("entity_type", entityType);

      if (entityId) {
        query.eq("entity_id", entityId);
      } else {
        query.is("entity_id", null);
      }

      await query;
    } catch (error) {
      console.error("Clear draft error:", error);
    }
  }, [entityType, entityId]);

  return {
    status,
    lastSaved,
    loadDraft,
    clearDraft,
    saveDraft,
  };
}
